import { CONFIG_VERSION, defaultConfig, defaultRecipe, tunedParamsOf, validateRecipe, withRecipe, withTuned,
  type PredictorConfig, type Recipe, type TunedParams } from "../engine/config";
import type { IsoDateTime } from "../engine/dates";
import { ACTIVE_CONFIG_VERSION, RECIPE, type ConfigVersion, type ConfigVersionKind, type Repositories } from "../storage/repositories";

/**
 * The config the app predicts with: defaults ← the active tuned parameters ← the user's recipe.
 *
 * Tuned parameters live in the append-only configVersions store. The active one is named in settings by its version
 * string ("2026-10-06.1+v3"), which is also what new shots are stamped with; no active row means the defaults
 * (stamped CONFIG_VERSION).
 */
export interface ActiveConfig {
  readonly config: PredictorConfig;
  readonly version: string;
  readonly recipe: Recipe;
  readonly params: TunedParams;
}

export function versionLabel(id: number): string {
  return `${CONFIG_VERSION}+v${id}`;
}

export async function loadRecipe(repos: Repositories): Promise<Recipe> {
  const raw = await repos.settings.get(RECIPE);
  if (raw === null) return defaultRecipe();
  try {
    const r = JSON.parse(raw) as Recipe;
    return validateRecipe({ doseG: Number(r.doseG), yieldG: Number(r.yieldG), timeLowS: Number(r.timeLowS), timeHighS: Number(r.timeHighS) });
  } catch {
    return defaultRecipe();
  }
}

/** Validates (throws RangeError with a message) and stores the recipe. */
export async function saveRecipe(repos: Repositories, r: Recipe): Promise<Recipe> {
  validateRecipe(r);
  await repos.settings.put(RECIPE, JSON.stringify(r));
  return r;
}

export async function activeVersion(repos: Repositories): Promise<ConfigVersion | null> {
  const v = await repos.settings.get(ACTIVE_CONFIG_VERSION);
  if (v === null) return null;
  return (await repos.configVersions.findAll()).find((x) => x.version === v) ?? null;
}

export async function loadActiveConfig(repos: Repositories, base: PredictorConfig = defaultConfig()): Promise<ActiveConfig> {
  const recipe = await loadRecipe(repos);
  const active = await activeVersion(repos);
  const params = active?.params ?? tunedParamsOf(base);
  let config: PredictorConfig;
  try {
    config = withRecipe(withTuned(base, params), recipe);
  } catch {
    config = withRecipe(base, recipe);
  }
  return { config, version: active?.version ?? CONFIG_VERSION, recipe, params };
}

/** Append a version row and make it active. */
export async function appendVersion(repos: Repositories, kind: ConfigVersionKind, params: TunedParams, now: IsoDateTime,
                                    scores: { before: number; after: number } | null = null): Promise<ConfigVersion> {
  const all = await repos.configVersions.findAll();
  const id = all.reduce((m, v) => Math.max(m, v.id as number), 0) + 1;
  const parent = (await repos.settings.get(ACTIVE_CONFIG_VERSION)) ?? CONFIG_VERSION;
  const saved = await repos.configVersions.save({
    id, version: versionLabel(id), createdAt: now, kind, params,
    scoreBefore: scores?.before ?? null, scoreAfter: scores?.after ?? null, parentVersion: parent,
  });
  await repos.settings.put(ACTIVE_CONFIG_VERSION, saved.version);
  return saved;
}

/** The parameters in force before the active version (defaults when it replaced the defaults). */
export async function previousParams(repos: Repositories, base: PredictorConfig = defaultConfig()): Promise<TunedParams | null> {
  const active = await activeVersion(repos);
  if (active === null) return null;
  const parent = (await repos.configVersions.findAll()).find((v) => v.version === active.parentVersion);
  return parent?.params ?? tunedParamsOf(base);
}

export async function revertToPrevious(repos: Repositories, now: IsoDateTime, base: PredictorConfig = defaultConfig()): Promise<ConfigVersion | null> {
  const prev = await previousParams(repos, base);
  return prev === null ? null : appendVersion(repos, "revert", prev, now);
}

export async function resetToDefaults(repos: Repositories, now: IsoDateTime, base: PredictorConfig = defaultConfig()): Promise<ConfigVersion> {
  return appendVersion(repos, "reset", tunedParamsOf(base), now);
}
